import { useState, useCallback } from "react";
import { SimpleForm, Button, required, useNotify } from "react-admin";
import { useForm } from "react-final-form";
import { GenericDateOrTimePicker } from "../../../common/form/GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress, Divider } from "@material-ui/core";
import { GetApp as GetAppIcon } from "@material-ui/icons";
import {
  EldFileExportApiFactory,
  EldFileExportDto,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { DriverSelectionList } from "../../../reports/safety/DriverSelectionList";
import { useSignalR } from "../../../../context/SignalRContext";
import { useRefresh } from "ra-core";
import { AxiosResponse } from "axios";

function formatDateToISOString(date: Date | null) {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const isoString = `${year}-${month}-${day}T00:00:00.000Z`;
  return isoString;
}

const useStyles = makeStyles((theme) => ({
  form: {
    "& > div": {
      width: "100%",
    },
  },
  dateContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0),
    width: "100%",
    marginBottom: theme.spacing(0),
    "& > div": {
      width: "100%",
    },
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-start",
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  button: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  icon: {
    marginRight: theme.spacing(0.5),
  },
}));

const FormToolbar = ({
  loading,
  onExport,
}: {
  loading: boolean;
  onExport: () => void;
}) => {
  const classes = useStyles();

  return (
    <>
      <Divider />
      <div className={classes.buttonContainer}>
        <Button
          label="Export"
          onClick={onExport}
          disabled={loading}
          className={classes.button}
        >
          {loading ? (
            <CircularProgress size={20} className={classes.icon} />
          ) : (
            <GetAppIcon className={classes.icon} />
          )}
        </Button>
      </div>
    </>
  );
};

interface ELDFileExportFormContentProps {
  onSuccess?: () => void;
}

const ELDFileExportFormContent = ({
  onSuccess,
}: ELDFileExportFormContentProps) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const axios = useAxiosInstance();
  const notify = useNotify();
  const form = useForm();
  const signalR = useSignalR();
  const refresh = useRefresh();

  const handleExport = useCallback(() => {
    if (loading || !axios) {
      return;
    }

    const formData = form.getState().values;

    if (
      !formData.driverIds ||
      !formData.driverIds.length ||
      !formData.dateFrom ||
      !formData.dateTo
    ) {
      notify("Please fill in all required fields", {
        type: "warning",
        messageArgs: {},
      });
      return;
    }

    const description = JSON.stringify({
      dateFrom: formData.dateFrom?.toISOString(),
      dateTo: formData.dateTo?.toISOString(),
      drivers: formData.driverIds.map((selectedDriverId: number) => {
        return {
          id: selectedDriverId,
        };
      }),
    });

    setLoading(true);
    EldFileExportApiFactory(undefined, axios?.defaults.baseURL, axios)
      .apiAppEldfileexportPost({
        driverIds: formData.driverIds,
        timeFrom: formatDateToISOString(formData.dateFrom),
        timeTo: formatDateToISOString(formData.dateTo),
        description: description,
      })
      .then((result: AxiosResponse<EldFileExportDto>) => {
        if (result.data) {
          notify("resources.admin-eld-export.messages.exportReady", {
            type: "success",
          });

          signalR.connection?.invoke(
            "SubscribeToSingleEldFileExportUpdates",
            result.data.id
          );

          refresh();
          onSuccess?.();
        }
      })
      .catch(() => {
        notify("resources.admin-eld-export.messages.exportFailed", {
          type: "error",
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, axios, loading, notify, onSuccess, signalR]);

  return (
    <>
      <div className={classes.dateContainer}>
        <GenericDateOrTimePicker
          PickerComponent={DatePicker}
          source="dateFrom"
          label="Date from"
          validate={required()}
          options={{
            autoOk: true,
            format: "MMMM do yyyy",
          }}
        />
        <GenericDateOrTimePicker
          PickerComponent={DatePicker}
          source="dateTo"
          label="Date to"
          validate={required()}
          options={{
            autoOk: true,
            format: "MMMM do yyyy",
          }}
        />
      </div>
      <DriverSelectionList source="driverIds" />
      <FormToolbar loading={loading} onExport={handleExport} />
    </>
  );
};

interface ELDFileExportFormProps {
  onSuccess?: () => void;
}

const ELDFileExportForm = ({ onSuccess }: ELDFileExportFormProps) => {
  const classes = useStyles();

  return (
    <SimpleForm
      className={classes.form}
      variant="outlined"
      redirect={false}
      toolbar={<span />}
    >
      <ELDFileExportFormContent onSuccess={onSuccess} />
    </SimpleForm>
  );
};

export default ELDFileExportForm;
